import React from 'react';
import Section from './Section';
import { siteData } from '../data/site';

const MapPinIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 0115 0z" />
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
  </svg>
);

const MailIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
  </svg>
);

const ClockIcon = () => (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const contactItems = (data) => [
  {
    Icon: MapPinIcon,
    title: 'Dirección',
    content: data.contact.address,
    link: `https://maps.google.com/?q=${encodeURIComponent(data.contact.address)}`,
    bg: 'bg-primary/10',
    color: 'text-primary',
  },
  {
    Icon: PhoneIcon,
    title: 'Teléfono',
    content: data.contact.phone,
    link: `tel:${data.contact.phone}`,
    bg: 'bg-violet-100',
    color: 'text-violet-600',
  },
  {
    Icon: MailIcon,
    title: 'Email',
    content: data.contact.email,
    link: `mailto:${data.contact.email}`,
    bg: 'bg-sky-100',
    color: 'text-sky-600',
  },
  {
    Icon: ClockIcon,
    title: 'Horarios',
    content: data.contact.hours,
    link: null,
    bg: 'bg-emerald-100',
    color: 'text-emerald-600',
  },
];

const Contact = () => {
  const items = contactItems(siteData);

  return (
    <Section id="contacto" className="bg-gradient-to-b from-[#fdf6ff] to-white">

      {/* Header */}
      <div className="text-center mb-16">
        <span className="badge bg-secondary/40 text-text mb-4">Estamos aquí</span>
        <h2 className="section-title mb-4">Contáctanos</h2>
        <p className="section-subtitle">
          Agenda tu consulta y descubre cómo podemos apoyar el desarrollo de tu hijo
        </p>
      </div>

      <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12">

        {/* Contact info */}
        <div className="space-y-5">
          <h3 className="font-fredoka text-2xl text-text mb-6">Información de contacto</h3>

          {items.map(({ Icon, title, content, link, bg, color }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-200"
            >
              <div className={`w-12 h-12 ${bg} rounded-2xl flex items-center justify-center flex-shrink-0`}>
                <span className={color}><Icon /></span>
              </div>
              <div>
                <h4 className="font-fredoka text-lg text-text leading-none mb-1">{title}</h4>
                {link ? (
                  <a
                    href={link}
                    target={link.startsWith('http') ? '_blank' : undefined}
                    rel={link.startsWith('http') ? 'noopener noreferrer' : undefined}
                    className="text-text/65 hover:text-primary transition-colors text-sm font-nunito"
                  >
                    {content}
                  </a>
                ) : (
                  <p className="text-text/65 text-sm font-nunito">{content}</p>
                )}
              </div>
            </div>
          ))}

          {/* WhatsApp CTA */}
          <button
            onClick={() => window.open('https://walink.co/eea878', '_blank')}
            className="w-full btn-primary mt-4"
          >
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
              <path d="M12 0C5.373 0 0 5.373 0 12c0 2.123.549 4.12 1.516 5.855L0 24l6.335-1.496A11.934 11.934 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.865 0-3.63-.482-5.17-1.33l-.37-.215-3.762.888.933-3.658-.24-.38A9.944 9.944 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
            </svg>
            Escríbenos por WhatsApp
          </button>
        </div>

        {/* Map */}
        <div className="space-y-4">
          <h3 className="font-fredoka text-2xl text-text mb-4">Nuestra ubicación</h3>
          <div className="bg-white rounded-3xl shadow-md overflow-hidden border border-gray-100">
            <iframe
              src="https://maps.google.com/maps?q=Estoril+50,+Las+Condes,+Santiago,+Chile&output=embed&hl=es&z=16"
              width="100%"
              height="380"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Ubicación del Centro Volantín"
              className="w-full"
            />
          </div>
          <p className="text-xs text-text/50 font-nunito text-center">
            {siteData.contact.address}
          </p>
        </div>
      </div>
    </Section>
  );
};

export default Contact;
